import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import api, { BACKEND_URL } from '@/lib/api';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import {
  ArrowLeft, Package, Calendar, Hash, User, CheckCircle2,
  Clock, Upload, FileText, Download, Shield, Factory,
  FlaskConical, ChevronDown, ChevronRight, ImagePlus, Camera,
  FolderOpen, Send, Maximize2, X, FileCheck
} from 'lucide-react';

const statusConfig = {
  'On Track': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'At Risk': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  'Delayed': { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

const DPR_STAGES = ['Cutting', 'Stitching', 'Finishing'];
const COMPLIANCE_SECTIONS = ['Fabric', 'Cutting', 'Stitching', 'Quality', 'Finishing'];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [expandedCompliance, setExpandedCompliance] = useState({});
  const [dprForm, setDprForm] = useState({ stage: 'Cutting', quantity: '', remarks: '' });
  const [savingDPR, setSavingDPR] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [savingPilot, setSavingPilot] = useState(false);
  const [pilotForm, setPilotForm] = useState({ pilot_sent: false, pilot_status: '', pilot_remarks: '', pattern_revision_count: 0 });
  const [imageExpanded, setImageExpanded] = useState(false);
  const complianceRefs = useRef({});
  const samRef = useRef(null);
  const imageRef = useRef(null);
  const pilotRef = useRef(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isMerchant = user?.role === 'merchant';
  const canUpload = isAdmin || isMerchant;

  const getBackPath = useCallback(() => {
    switch (user?.role) {
      case 'super_admin': case 'admin': return '/dashboard/admin';
      case 'merchant': return '/dashboard/merchant';
      case 'buyer': return '/dashboard/buyer';
      default: return '/dashboard/internal';
    }
  }, [user?.role]);

  const loadOrder = useCallback(async () => {
    try {
      const res = await api.get(`/orders/${id}`);
      setOrder(res.data);
      const p = res.data.pilot || {};
      setPilotForm({ pilot_sent: p.sent || false, pilot_status: p.status || '', pilot_remarks: p.remarks || '', pattern_revision_count: p.pattern_revision_count || 0 });
    } catch (e) {
      toast.error('Failed to load order');
      navigate(getBackPath());
    }
    setLoading(false);
  }, [id, navigate, getBackPath]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const handleComplianceSectionUpload = async (e, section) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const key = `compliance-${section}`;
    setUploading(prev => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/orders/${id}/files?stage=${encodeURIComponent(section)}&category=compliance`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadOrder();
      toast.success(`Compliance doc uploaded to ${section}`);
    } catch (e) { toast.error('Upload failed'); }
    setUploading(prev => ({ ...prev, [key]: false }));
    if (complianceRefs.current[section]) complianceRefs.current[section].value = '';
  };

  const handleSamUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, sam: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/orders/${id}/files?stage=General&category=sam_study`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadOrder();
      toast.success('SAM Study uploaded');
    } catch (e) { toast.error('Upload failed'); }
    setUploading(prev => ({ ...prev, sam: false }));
    if (samRef.current) samRef.current.value = '';
  };

  const handlePilotUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(prev => ({ ...prev, pilot: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/orders/${id}/files?stage=General&category=pilot`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadOrder();
      toast.success('Pilot document uploaded');
    } catch (e) { toast.error('Upload failed'); }
    setUploading(prev => ({ ...prev, pilot: false }));
    if (pilotRef.current) pilotRef.current.value = '';
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/orders/${id}/image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await loadOrder();
      toast.success('Order image uploaded');
    } catch (e) { toast.error('Image upload failed'); }
    setUploadingImage(false);
    if (imageRef.current) imageRef.current.value = '';
  };

  const handleDPRSubmit = async () => {
    if (!dprForm.quantity || parseInt(dprForm.quantity) <= 0) { toast.error('Enter a valid quantity'); return; }
    setSavingDPR(true);
    try {
      await api.post('/dpr', { order_id: id, stage: dprForm.stage, quantity: parseInt(dprForm.quantity, 10), date: new Date().toISOString().split('T')[0], remarks: dprForm.remarks });
      toast.success(`${dprForm.stage}: ${dprForm.quantity} pcs logged`);
      setDprForm({ stage: 'Cutting', quantity: '', remarks: '' });
      await loadOrder();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingDPR(false);
  };

  const handlePilotSave = async () => {
    setSavingPilot(true);
    try {
      await api.put(`/orders/${id}/pilot`, pilotForm);
      await loadOrder();
      toast.success('Pilot info updated');
    } catch (e) { toast.error('Failed to update pilot'); }
    setSavingPilot(false);
  };

  const downloadFile = (fileId) => {
    const token = localStorage.getItem('gh_token');
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    window.open(`${baseUrl}/api/files/${fileId}/download?auth=${token}`, '_blank');
  };

  const toggleCompliance = (section) => setExpandedCompliance(prev => ({ ...prev, [section]: !prev[section] }));

  if (loading) return <DashboardLayout><div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-[#1C1C1C] border-t-transparent rounded-full animate-spin" /></div></DashboardLayout>;
  if (!order) return null;

  const sc = statusConfig[order.status] || statusConfig['On Track'];
  const complianceFiles = (order.files || []).filter(f => f.category === 'compliance');
  const samFiles = (order.files || []).filter(f => f.category === 'sam_study');
  const pilotFiles = (order.files || []).filter(f => f.category === 'pilot');
  const dprEntries = order.dpr_entries || [];
  const pilot = order.pilot || {};

  const complianceBySub = {};
  COMPLIANCE_SECTIONS.forEach(s => { complianceBySub[s] = []; });
  complianceFiles.forEach(f => { const sub = f.stage || 'General'; if (complianceBySub[sub]) complianceBySub[sub].push(f); });

  return (
    <DashboardLayout>
      <div data-testid="order-detail-page" className="max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <button onClick={() => navigate(getBackPath())} className="inline-flex items-center gap-1.5 text-sm text-[#52525B] hover:text-[#111111] mb-4 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back to Orders
            </button>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-[#0A0A0A] tracking-tight">{order.order_id}</h1>
            <p className="text-sm text-[#52525B] mt-1">
              {order.style_name}{order.buyer_name ? ` | ${order.buyer_name}` : ''}{order.merchant_name ? ` | Merchant: ${order.merchant_name}` : ''}
            </p>
          </div>
          <div className={`status-badge ${sc.bg} ${sc.text} text-sm`}><div className={`w-2 h-2 rounded-full ${sc.dot}`} />{order.status}</div>
        </div>

        {/* Order Image */}
        <div className="bg-white rounded-md border border-black/[0.06] overflow-hidden" data-testid="order-image-section">
          {order.order_image ? (
            <div className="relative h-48 sm:h-64 lg:h-72 bg-[#F4F4F5] flex items-center justify-center">
              <img
                src={`${BACKEND_URL}/api/orders/${id}/image/serve?auth=${localStorage.getItem('gh_token')}`}
                alt={order.style_name}
                className="max-w-full max-h-full object-contain cursor-pointer transition-transform hover:scale-[1.02]"
                onClick={() => setImageExpanded(true)}
                data-testid="order-image"
              />
              <div className="absolute top-3 right-3 flex gap-2">
                <button onClick={() => setImageExpanded(true)} className="p-1.5 rounded-md bg-white/90 backdrop-blur border border-black/[0.08] hover:bg-white transition-colors" data-testid="expand-image-btn">
                  <Maximize2 className="w-3.5 h-3.5 text-[#52525B]" />
                </button>
              </div>
              {canUpload && (
                <div className="absolute bottom-3 right-3">
                  <input ref={imageRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="change-image" data-testid="change-image-input" />
                  <label htmlFor="change-image"><Button variant="outline" size="sm" className="rounded-md text-xs bg-white/90 backdrop-blur" asChild disabled={uploadingImage}><span className="cursor-pointer"><Camera className="w-3 h-3 mr-1.5" />{uploadingImage ? 'Uploading...' : 'Change Image'}</span></Button></label>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-48 bg-[#FAFAF8]">
              {canUpload ? (
                <><input ref={imageRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="upload-order-image" data-testid="upload-image-input" />
                <label htmlFor="upload-order-image" className="flex flex-col items-center cursor-pointer group">
                  <div className="w-14 h-14 rounded-full bg-black/[0.04] flex items-center justify-center mb-3 group-hover:bg-black/[0.08] transition-colors"><ImagePlus className="w-6 h-6 text-[#A1A1AA] group-hover:text-[#52525B] transition-colors" /></div>
                  <p className="text-sm font-medium text-[#71717A]">{uploadingImage ? 'Uploading...' : 'Upload Order Image'}</p>
                  <p className="text-xs text-[#A1A1AA] mt-1">Click to add a product photo</p>
                </label></>
              ) : (
                <div className="flex flex-col items-center"><Camera className="w-8 h-8 text-[#D4D4D8] mb-2" /><p className="text-xs text-[#A1A1AA]">No image uploaded</p></div>
              )}
            </div>
          )}
        </div>

        {/* Image Lightbox */}
        {imageExpanded && order.order_image && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setImageExpanded(false)} data-testid="image-lightbox">
            <button onClick={() => setImageExpanded(false)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-testid="close-lightbox-btn">
              <X className="w-6 h-6 text-white" />
            </button>
            <img
              src={`${BACKEND_URL}/api/orders/${id}/image/serve?auth=${localStorage.getItem('gh_token')}`}
              alt={order.style_name}
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="lightbox-image"
            />
          </div>
        )}

        {/* Order Summary */}
        <div className="bg-white rounded-md border border-black/[0.06] p-6">
          <h2 className="text-sm font-semibold text-[#0A0A0A] mb-5">Order Summary</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            <InfoItem icon={Hash} label="PA NO" value={order.pa_no || '-'} />
            <InfoItem icon={Package} label="Quantity" value={order.quantity?.toLocaleString()} />
            <InfoItem icon={Calendar} label="Delivery" value={order.delivery_date ? new Date(order.delivery_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A'} />
            <InfoItem icon={Calendar} label="Inspection" value={order.inspection_date ? new Date(order.inspection_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A'} />
            {order.merchant_name && <InfoItem icon={User} label="Merchant" value={order.merchant_name} />}
            {order.factory_name && <InfoItem icon={Factory} label="Factory" value={order.factory_name} />}
            {order.department && <InfoItem icon={Package} label="Department" value={order.department} />}
            <InfoItem icon={FileCheck} label="File Sent" value={order.file_sent ? 'Yes' : 'No'} />
          </div>
          {(order.value_additions || order.remarks || order.file_sent_remarks) && (
            <div className="mt-5 pt-5 border-t border-black/[0.04] grid grid-cols-1 sm:grid-cols-3 gap-4">
              {order.value_additions && typeof order.value_additions === 'object' && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-semibold">Value Additions</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {order.value_additions.printing && <span className="text-xs font-medium bg-[#F5F5F5] text-[#0A0A0A] px-2 py-0.5 rounded">Printing</span>}
                    {order.value_additions.embroidery && <span className="text-xs font-medium bg-[#F5F5F5] text-[#0A0A0A] px-2 py-0.5 rounded">Embroidery</span>}
                    {order.value_additions.garment_wash && <span className="text-xs font-medium bg-[#F5F5F5] text-[#0A0A0A] px-2 py-0.5 rounded">Garment Wash</span>}
                    {order.value_additions.others && <span className="text-xs font-medium bg-[#F5F5F5] text-[#0A0A0A] px-2 py-0.5 rounded">{order.value_additions.others}</span>}
                  </div>
                </div>
              )}
              {order.file_sent_remarks && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-semibold">File Sent Remarks</p>
                  <p className="text-sm font-medium text-[#0A0A0A] mt-0.5">{order.file_sent_remarks}</p>
                </div>
              )}
              {order.remarks && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-semibold">Remarks</p>
                  <p className="text-sm font-medium text-[#0A0A0A] mt-0.5">{order.remarks}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pilot Documents */}
        <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="pilot-section">
          <div className="flex items-center gap-2 mb-5">
            <Send className="w-4 h-4 text-[#C2A878]" />
            <h2 className="text-sm font-semibold text-[#0A0A0A]">Pilot Documents</h2>
          </div>
          {canUpload ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Pilot Sent</p>
                  <Select value={pilotForm.pilot_sent ? 'yes' : 'no'} onValueChange={(v) => setPilotForm(p => ({ ...p, pilot_sent: v === 'yes' }))}>
                    <SelectTrigger className="h-9 rounded-md text-sm" data-testid="pilot-sent-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Pilot Status</p>
                  <Select value={pilotForm.pilot_status || 'Not Initiated'} onValueChange={(v) => setPilotForm(p => ({ ...p, pilot_status: v }))}>
                    <SelectTrigger className="h-9 rounded-md text-sm" data-testid="pilot-status-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Not Initiated">Not Initiated</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Pattern Revisions</p>
                  <Input type="number" min="0" value={pilotForm.pattern_revision_count} onChange={(e) => setPilotForm(p => ({ ...p, pattern_revision_count: parseInt(e.target.value) || 0 }))} placeholder="0" className="h-9 rounded-md text-sm text-center font-semibold" data-testid="pilot-revision-input" />
                </div>
                <div>
                  <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Pilot Remarks</p>
                  <Input value={pilotForm.pilot_remarks} onChange={(e) => setPilotForm(p => ({ ...p, pilot_remarks: e.target.value }))} placeholder="Notes..." className="h-9 rounded-md text-sm" data-testid="pilot-remarks-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={handlePilotSave} disabled={savingPilot} size="sm" className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md text-xs h-8 px-4" data-testid="pilot-save-btn">
                  {savingPilot ? 'Saving...' : 'Save Pilot Info'}
                </Button>
                <div>
                  <input ref={pilotRef} type="file" onChange={handlePilotUpload} className="hidden" id="upload-pilot" data-testid="upload-pilot-input" />
                  <label htmlFor="upload-pilot">
                    <Button variant="outline" size="sm" className="rounded-md text-xs h-8" asChild disabled={uploading.pilot}>
                      <span className="cursor-pointer"><Upload className="w-3 h-3 mr-1.5" />{uploading.pilot ? 'Uploading...' : 'Upload Pilot Doc'}</span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Pilot Sent</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.sent ? 'Yes' : 'No'}</p></div>
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Status</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.status || 'Not Initiated'}</p></div>
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Pattern Revisions</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.pattern_revision_count || 0}</p></div>
              <div><p className="text-[#A1A1AA] font-semibold uppercase tracking-wider text-[10px]">Remarks</p><p className="font-medium text-[#0A0A0A] mt-0.5">{pilot.remarks || '-'}</p></div>
            </div>
          )}
          {pilotFiles.length > 0 && (
            <div className="mt-4 pt-4 border-t border-black/[0.04] space-y-1.5">
              <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-2">Pilot Files</p>
              {pilotFiles.map(f => <FileRow key={f.id} file={f} onDownload={downloadFile} color="text-[#C2A878]" />)}
            </div>
          )}
        </div>

        {/* Production Insights */}
        {order.dpr_summary && (order.dpr_summary.cutting > 0 || order.dpr_summary.stitching > 0 || order.dpr_summary.finishing > 0) && (
          <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="production-insights">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-6">Production Insights</h2>
            <div className="grid grid-cols-3 gap-4">
              <DPRPieCard label="Cutting" done={order.dpr_summary.cutting} remaining={order.dpr_summary.cutting_remaining} color="#3B82F6" />
              <DPRPieCard label="Stitching" done={order.dpr_summary.stitching} remaining={order.dpr_summary.stitching_remaining} color="#F59E0B" />
              <DPRPieCard label="Finishing" done={order.dpr_summary.finishing} remaining={order.dpr_summary.finishing_remaining} color="#10B981" />
            </div>
          </div>
        )}

        {/* DPR Entry */}
        {canUpload && (
          <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="dpr-entry-section">
            <h2 className="text-sm font-semibold text-[#0A0A0A] mb-4">Daily Production Update</h2>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Stage</p>
                <Select value={dprForm.stage} onValueChange={(v) => setDprForm(p => ({ ...p, stage: v }))}><SelectTrigger className="h-9 rounded-md text-sm" data-testid="dpr-stage-select"><SelectValue /></SelectTrigger><SelectContent>{DPR_STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="w-28">
                <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Quantity</p>
                <Input type="number" min="1" value={dprForm.quantity} onChange={(e) => setDprForm(p => ({ ...p, quantity: e.target.value }))} placeholder="0" className="h-9 rounded-md text-sm text-center font-semibold" data-testid="dpr-qty-input" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-1.5">Remarks</p>
                <Input value={dprForm.remarks} onChange={(e) => setDprForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Optional notes..." className="h-9 rounded-md text-sm" data-testid="dpr-remarks-input" />
              </div>
              <Button onClick={handleDPRSubmit} disabled={savingDPR} className="bg-[#1C1C1C] hover:bg-[#0A0A0A] text-white rounded-md h-9 px-5 text-sm font-semibold btn-premium flex-shrink-0" data-testid="dpr-submit-btn">{savingDPR ? '...' : 'Log'}</Button>
            </div>
            {dprEntries.length > 0 && (
              <div className="mt-4 pt-4 border-t border-black/[0.04]">
                <p className="text-[10px] text-[#A1A1AA] font-semibold uppercase tracking-wider mb-2">Recent Entries</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {dprEntries.slice(0, 10).map(entry => (
                    <div key={entry.id} className="flex items-center justify-between text-xs bg-[#FAFAF8] rounded-md px-3 py-2">
                      <div className="flex items-center gap-3"><span className="font-semibold text-[#0A0A0A]">{entry.stage}</span><span className="text-[#52525B]">{entry.quantity.toLocaleString()} pcs</span>{entry.remarks && <span className="text-[#A1A1AA] truncate max-w-[150px]">{entry.remarks}</span>}</div>
                      <div className="flex items-center gap-2 text-[#A1A1AA] flex-shrink-0"><span>{entry.submitted_by_name || 'Unknown'}</span><span>{entry.date}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compliance Documents */}
        <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="compliance-section">
          <div className="flex items-center gap-2 mb-5"><Shield className="w-4 h-4 text-[#6B7D5C]" /><h2 className="text-sm font-semibold text-[#0A0A0A]">Compliance Documents</h2><span className="text-[11px] text-[#A1A1AA] ml-1">Visible to buyers</span></div>
          <div className="space-y-1">
            {COMPLIANCE_SECTIONS.map((section) => {
              const sectionFiles = complianceBySub[section] || [];
              const isOpen = expandedCompliance[section];
              const uploadKey = `compliance-${section}`;
              return (
                <div key={section} className="border border-black/[0.04] rounded-lg overflow-hidden" data-testid={`compliance-sub-${section.toLowerCase()}`}>
                  <button onClick={() => toggleCompliance(section)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FAFAF8] transition-colors text-left" data-testid={`compliance-toggle-${section.toLowerCase()}`}>
                    <div className="flex items-center gap-3"><FolderOpen className="w-4 h-4 text-[#6B7D5C]" /><span className="text-sm font-medium text-[#0A0A0A]">{section}</span><span className="text-[10px] text-[#A1A1AA] font-medium bg-[#F5F5F5] px-1.5 py-0.5 rounded">{sectionFiles.length}</span></div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#A1A1AA]" /> : <ChevronRight className="w-4 h-4 text-[#A1A1AA]" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t border-black/[0.04] bg-[#FAFAF8]">
                      {sectionFiles.length === 0 ? <p className="text-xs text-[#A1A1AA] py-2">No documents uploaded</p> : <div className="space-y-1.5 mb-3">{sectionFiles.map(f => <FileRow key={f.id} file={f} onDownload={downloadFile} color="text-[#6B7D5C]" />)}</div>}
                      {canUpload && (
                        <div><input ref={el => complianceRefs.current[section] = el} type="file" onChange={(e) => handleComplianceSectionUpload(e, section)} className="hidden" id={`upload-compliance-${section}`} data-testid={`upload-compliance-${section.toLowerCase()}`} /><label htmlFor={`upload-compliance-${section}`}><Button variant="outline" size="sm" className="rounded-md text-xs h-7" asChild disabled={uploading[uploadKey]}><span className="cursor-pointer"><Upload className="w-3 h-3 mr-1.5" />{uploading[uploadKey] ? 'Uploading...' : `Upload to ${section}`}</span></Button></label></div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* SAM Study */}
        <div className="bg-white rounded-md border border-black/[0.06] p-6" data-testid="sam-section">
          <div className="flex items-center gap-2 mb-5"><FlaskConical className="w-4 h-4 text-[#6B7D5C]" /><h2 className="text-sm font-semibold text-[#0A0A0A]">SAM Study Reports</h2><span className="text-[11px] text-[#A1A1AA] ml-1">Visible to buyers</span></div>
          {samFiles.length === 0 ? <p className="text-sm text-[#A1A1AA] py-4">No SAM study reports uploaded yet</p> : <div className="space-y-2">{samFiles.map(f => <FileRow key={f.id} file={f} onDownload={downloadFile} color="text-[#6B7D5C]" large />)}</div>}
          {canUpload && (
            <div className="mt-4 pt-4 border-t border-black/[0.04]">
              <input ref={samRef} type="file" onChange={handleSamUpload} className="hidden" id="upload-sam" data-testid="upload-sam-input" />
              <label htmlFor="upload-sam"><Button variant="outline" className="rounded-md text-sm" asChild disabled={uploading.sam}><span className="cursor-pointer"><Upload className="w-4 h-4 mr-2" />{uploading.sam ? 'Uploading...' : 'Upload SAM Study'}</span></Button></label>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3"><Icon className="w-4 h-4 text-[#A1A1AA] mt-0.5" /><div><p className="text-[11px] uppercase tracking-wide text-[#A1A1AA] font-semibold">{label}</p><p className="text-sm font-semibold text-[#0A0A0A] mt-0.5">{value}</p></div></div>
  );
}

function FileRow({ file, onDownload, color = 'text-[#C2A878]', large = false }) {
  return (
    <div className={`flex items-center justify-between ${large ? 'bg-[#FAFAFA] rounded-md px-4 py-3 border border-black/[0.04]' : 'bg-white rounded px-3 py-2 border border-black/[0.04]'}`} data-testid={`file-${file.id}`}>
      <div className="flex items-center gap-3 min-w-0"><FileText className={`w-4 h-4 ${color} flex-shrink-0`} /><div className="min-w-0"><p className={`${large ? 'text-sm' : 'text-xs'} font-medium text-[#0A0A0A] truncate`}>{file.original_filename}</p><p className="text-[10px] text-[#A1A1AA]">{file.uploaded_by_name || 'Unknown'} &middot; {new Date(file.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</p></div></div>
      <button onClick={() => onDownload(file.id)} className="p-1.5 rounded-md hover:bg-black/5 text-[#52525B] transition-colors flex-shrink-0" data-testid={`download-${file.id}`}><Download className={`${large ? 'w-4 h-4' : 'w-3.5 h-3.5'}`} /></button>
    </div>
  );
}

function DPRPieCard({ label, done, remaining, color }) {
  const total = done + remaining;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const data = total > 0 ? [{ name: 'Done', value: done }, { name: 'Remaining', value: remaining }] : [{ name: 'No Data', value: 1 }];
  const colors = total > 0 ? [color, '#F0F0ED'] : ['#E4E4E7'];
  return (
    <div className="text-center" data-testid={`dpr-pie-${label.toLowerCase()}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-[#71717A] mb-3">{label}</p>
      <div className="relative w-[110px] h-[110px] mx-auto">
        <ResponsiveContainer width={110} height={110}><PieChart><Pie data={data} cx="50%" cy="50%" innerRadius={33} outerRadius={50} dataKey="value" strokeWidth={0} paddingAngle={2}>{data.map((d, i) => <Cell key={d.name} fill={colors[i]} />)}</Pie><Tooltip content={({ active, payload }) => { if (!active || !payload?.length) return null; return (<div className="bg-white border border-black/[0.08] rounded-lg px-3 py-1.5 shadow-lg text-xs"><span className="font-medium">{payload[0].name}: {payload[0].value.toLocaleString()}</span></div>); }} /></PieChart></ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><p className="font-heading text-lg font-semibold text-[#0A0A0A]">{pct}%</p></div>
      </div>
      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-center gap-2"><div className="w-2 h-2 rounded-full" style={{ background: color }} /><span className="text-xs text-[#52525B]">Done: <span className="font-semibold text-[#0A0A0A]">{done.toLocaleString()}</span></span></div>
        <div className="flex items-center justify-center gap-2"><div className="w-2 h-2 rounded-full bg-[#F0F0ED]" /><span className="text-xs text-[#52525B]">Remaining: <span className="font-semibold text-[#0A0A0A]">{remaining.toLocaleString()}</span></span></div>
      </div>
    </div>
  );
}
